import { isComponentUriDecoded, isComponentUriEncoded } from './componentUriCoding'

/**
 * Safely decodes an encoded URI component.
 * Accepts an input known to be an EncodedURIComponentString and returns a DecodedURIComponentString.
 */
export function safeDecodeURIComponent(
  encoded: EncodedURIComponentString,
): DecodedURIComponentString {
  const decoded = decodeURIComponent(encoded)

  return decoded as DecodedURIComponentString
}

/**
 * Safely encodes a decoded URI component.
 * Accepts an input known to be a DecodedURIComponentString and returns an EncodedURIComponentString.
 */
export function safeEncodeURIComponent(
  decoded: DecodedURIComponentString,
): EncodedURIComponentString {
  const encoded = encodeURIComponent(decoded)

  return encoded as EncodedURIComponentString
}

/**
 * Ensures that a raw string is returned as a DecodedURIComponentString.
 * If the input is URI encoded, it will be safely decoded.
 * Otherwise, if it is already decoded, it returns it as is.
 *
 * @param input - The raw URI component string.
 * @returns The string typed as a DecodedURIComponentString.
 */
export function ensureDecodedURIComponent(input: string): DecodedURIComponentString {
  if (isComponentUriEncoded(input)) {
    return safeDecodeURIComponent(input)
  }

  if (isComponentUriDecoded(input)) {
    return input
  }

  throw new Error('Invalid URI component')
}

/**
 * Sanitizes a decoded URI component by normalizing its Unicode representation (NFKC)
 * and trimming whitespace from both ends.
 * Returns null if the resulting string is empty.
 *
 * @param decoded - The decoded URI component string.
 * @returns The sanitized string or null.
 */
export function sanitizeDecodedURIComponent(
  decoded: DecodedURIComponentString,
): DecodedURIComponentString | null {
  const normalized = decoded.normalize('NFKC')
  const trimmed = normalized.trim()

  return trimmed.length > 0 ? (trimmed as DecodedURIComponentString) : null
}
